import { type Ref, useMemo } from "react";

import {
  Alert,
  AudienceIcon,
  Box,
  Column,
  EmptyState,
  IconBox,
  Row,
  Text,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";

import boxPlaceholder from "src/assets/placeholders/box.svg";
import { columnReferenceKey } from "src/components/explore/filter-popover/utils";
import { RealtimeBadge } from "src/components/realtime-badge";
import {
  type ColumnOption,
  useFormkitContext,
} from "src/formkit/components/formkit-context";
import { CantFindColumnMessage } from "src/formkit/components/mapper-v2/cant-find-column-message";
import {
  type ColumnCategory,
  filterColumnOptions,
  getOptionDisplayLabel,
  getOptionSourceMeta,
  type IconMeta,
  isAudienceTraitOption,
} from "src/formkit/components/mapper-v2/column-categories";
import { useMapperListbox } from "src/formkit/components/mapper-v2/hooks/use-mapper-listbox";
import {
  MapperList,
  MapperListRow,
  MapperRowIcon,
  MapperSearch,
} from "src/formkit/components/mapper-v2/mapper-list";
import {
  getValueType,
  type MapperValue,
} from "src/formkit/components/mapper-v2/mapper-selection";
import { MappingType } from "src/formkit/components/types";
import { extractColumnValues } from "src/formkit/components/utils";
import { usePersonalization } from "src/hooks/use-personalization";
import { convertType, NEW_ICON_MAP } from "src/utils/destinations";

type ColumnPaneProps = {
  /** Undefined when no column category is available to pick from. */
  category: ColumnCategory | undefined;
  /** Every column group, so a typed event column is only offered when it is new. */
  columnGroups: ColumnOption[] | undefined;
  /** The committed mapping value, whose column is shown as chosen. */
  value: MapperValue;
  /** The mapping type a chosen column is saved with. */
  draftType: MappingType;
  search: string;
  onSearchChange: (search: string) => void;
  searchRef: Ref<HTMLInputElement>;
  onChoose: (value: MapperValue) => void;
  onClose: () => void;
};

type ColumnRow =
  | { kind: "option"; key: string; option: ColumnOption; isSelected: boolean }
  | { kind: "expected"; key: string; name: string; isSelected: boolean };

/** The list of one column category, with search. */
export function ColumnPane({
  category,
  columnGroups,
  value,
  draftType,
  search,
  onSearchChange,
  searchRef,
  onChoose,
  onClose,
}: ColumnPaneProps) {
  const { model, variableScope } = useFormkitContext();
  const isEventModel = Boolean(model?.isEventModel);
  const {
    value: isCantFindColumnDismissed,
    setLocal: setIsCantFindColumnDismissed,
  } = usePersonalization<boolean>({
    key: "syncs.cant-find-column-dismissed",
    initialValue: false,
    useWorkspace: false,
  });

  const optionKeys = useMemo(
    () =>
      new Map(
        (category?.options ?? []).map((option) => [
          option,
          columnReferenceKey(option.value),
        ]),
      ),
    [category],
  );
  // Ungrouped options save their label, not their value.
  const bareOptions = useMemo(
    () => new Set((columnGroups ?? []).filter((group) => !group.options)),
    [columnGroups],
  );
  const existingValues = useMemo(
    () => extractColumnValues(columnGroups),
    [columnGroups],
  );

  const isColumnMapping = getValueType(value) === MappingType.STANDARD;
  const committedKey =
    isColumnMapping && value.from !== undefined
      ? columnReferenceKey(value.from)
      : undefined;
  const isCommitted = (option: ColumnOption) =>
    bareOptions.has(option)
      ? isColumnMapping && value.from === option.label
      : committedKey !== undefined && optionKeys.get(option) === committedKey;

  // An expected event column that the suggestions don't list keeps a chosen row of its own.
  const unlistedCommittedFrom =
    isEventModel &&
    isColumnMapping &&
    typeof value.from === "string" &&
    value.from &&
    !existingValues.has(value.from)
      ? value.from
      : undefined;

  const additionalColumns = model?.visual_query_filter?.additionalColumns;
  const trimmedSearch = search.trim();
  const rows: ColumnRow[] = [
    ...(isEventModel &&
    trimmedSearch &&
    !existingValues.has(trimmedSearch) &&
    trimmedSearch !== unlistedCommittedFrom
      ? [
          {
            kind: "expected" as const,
            key: `create:${trimmedSearch}`,
            name: trimmedSearch,
            isSelected: false,
          },
        ]
      : []),
    ...filterColumnOptions(
      category?.options ?? [],
      trimmedSearch,
      additionalColumns,
    ).map(
      (option): ColumnRow => ({
        kind: "option",
        // An ungrouped option's value can equal a grouped column's reference key.
        key: bareOptions.has(option)
          ? `label:${option.label}`
          : `column:${optionKeys.get(option) ?? option.label}`,
        option,
        isSelected: isCommitted(option),
      }),
    ),
    ...(unlistedCommittedFrom
      ? [
          {
            kind: "expected" as const,
            key: `committed:${unlistedCommittedFrom}`,
            name: unlistedCommittedFrom,
            isSelected: true,
          },
        ]
      : []),
  ];

  const chooseRow = (row: ColumnRow) => {
    if (row.isSelected) {
      onClose();
    } else if (row.kind === "expected") {
      onChoose({ type: draftType, from: row.name });
    } else if (bareOptions.has(row.option)) {
      onChoose({ type: draftType, from: row.option.label });
    } else {
      onChoose({
        type: draftType,
        from: row.option.value,
        alias:
          row.option.suggestedName ??
          (row.option.value !== row.option.label
            ? row.option.label
            : undefined),
      });
    }
  };

  const listbox = useMapperListbox({
    optionCount: rows.length,
    hasSearch: Boolean(trimmedSearch),
    onChoose: (index) => {
      const row = rows[index];
      if (row) chooseRow(row);
    },
  });

  const searchLabel =
    isEventModel && !category?.options.length
      ? "Enter an expected column"
      : "Search columns";

  const showCantFindColumn =
    !isCantFindColumnDismissed &&
    !isEventModel &&
    variableScope !== "realtime-journey";

  return (
    <>
      <MapperSearch
        inputRef={searchRef}
        label={searchLabel}
        listbox={listbox}
        placeholder={`${searchLabel}…`}
        value={search}
        onChange={(nextSearch) => {
          onSearchChange(nextSearch);
          listbox.resetActiveIndex();
        }}
      />
      {category?.kind === "audience-traits" && (
        <Row
          alignItems="center"
          borderBottomWidth="hairline"
          borderColor="base.border"
          borderStyle="solid"
          borderTopWidth="hairline"
          flexShrink={0}
          gap={2}
          justifyContent="space-between"
          px={4}
          py={2}
        >
          <Row alignItems="center" gap={2} minWidth={0}>
            <Box
              as={AudienceIcon}
              boxSize={4}
              color="icon.tertiary"
              flexShrink={0}
              m={0.5}
            />
            <TextWithTooltip
              color="text.tertiary"
              fontWeight="medium"
              size="sm"
            >
              {model?.name ?? ""}
            </TextWithTooltip>
          </Row>
          <Box flexShrink={0}>
            <Text color="text.tertiary" size="sm">
              Only available in this audience
            </Text>
          </Box>
        </Row>
      )}
      {rows.length > 0 ? (
        <MapperList
          activeOptionId={listbox.activeOptionId}
          header={
            showCantFindColumn && (
              <Alert
                variant="inline"
                type="subtle"
                title="Can't find a column?"
                message={<CantFindColumnMessage />}
                mb={2}
                onDismiss={() => setIsCantFindColumnDismissed(true)}
              />
            )
          }
          label={category?.label ?? searchLabel}
          listId={listbox.listId}
          scrollRef={listbox.scrollRef}
        >
          {rows.map((row, index) =>
            row.kind === "expected" ? (
              <MapperListRow
                key={row.key}
                id={listbox.getOptionId(index)}
                isActive={index === listbox.activeIndex}
                isSelected={row.isSelected}
                label={`Use expected column name "${row.name}"…`}
                onChoose={() => chooseRow(row)}
              />
            ) : (
              <ColumnOptionRow
                key={row.key}
                displayLabel={getOptionDisplayLabel(
                  row.option,
                  additionalColumns,
                )}
                id={listbox.getOptionId(index)}
                isActive={index === listbox.activeIndex}
                isSelected={row.isSelected}
                option={row.option}
                showAudienceTraitMarker={
                  category?.kind === "all" && isAudienceTraitOption(row.option)
                }
                source={
                  category?.showSource
                    ? getOptionSourceMeta(row.option, additionalColumns)
                    : undefined
                }
                onChoose={() => chooseRow(row)}
              />
            ),
          )}
        </MapperList>
      ) : (
        <ColumnEmptyState
          hasCategory={Boolean(category)}
          hasSearch={Boolean(trimmedSearch)}
          isEventModel={isEventModel}
        />
      )}
    </>
  );
}

type ColumnOptionRowProps = {
  id: string;
  option: ColumnOption;
  displayLabel: string;
  /** Marks audience traits in views that mix them with other columns. */
  showAudienceTraitMarker: boolean;
  /** Where the option comes from, shown in views that mix sources. */
  source: (IconMeta & { name: string }) | undefined;
  isActive: boolean;
  isSelected: boolean;
  onChoose: () => void;
};

function ColumnOptionRow({
  id,
  option,
  displayLabel,
  showAudienceTraitMarker,
  source,
  isActive,
  isSelected,
  onChoose,
}: ColumnOptionRowProps) {
  const TypeIcon =
    NEW_ICON_MAP[convertType(option.customType || option.type || "")];

  return (
    <MapperListRow
      accessory={
        (option.isRealtimeTrait || showAudienceTraitMarker) && (
          <>
            {option.isRealtimeTrait && <RealtimeBadge size="sm" />}
            {showAudienceTraitMarker && (
              <Tooltip message="Audience trait">
                <Box
                  as={AudienceIcon}
                  aria-label="Audience trait"
                  boxSize={4}
                  color="icon.tertiary"
                  flexShrink={0}
                  role="img"
                />
              </Tooltip>
            )}
          </>
        )
      }
      description={option.description ?? undefined}
      icon={TypeIcon && <MapperRowIcon icon={TypeIcon} />}
      id={id}
      isActive={isActive}
      isSelected={isSelected}
      label={displayLabel}
      source={source && <SourceCell meta={source} />}
      onChoose={onChoose}
    />
  );
}

function SourceCell({ meta }: { meta: IconMeta & { name: string } }) {
  const Icon = meta.icon;
  return (
    <>
      <Row flexShrink={0}>
        <IconBox
          color={meta.color}
          icon={<Icon />}
          size="sm"
          variant={meta.variant}
        />
      </Row>
      <TextWithTooltip color="text.secondary" fontWeight="medium">
        {meta.name}
      </TextWithTooltip>
    </>
  );
}

type ColumnEmptyStateProps = {
  hasCategory: boolean;
  hasSearch: boolean;
  isEventModel: boolean;
};

function ColumnEmptyState({
  hasCategory,
  hasSearch,
  isEventModel,
}: ColumnEmptyStateProps) {
  return (
    <Column flex={1} justifyContent="center" p={6}>
      {hasSearch ? (
        <EmptyState
          imageUrl={boxPlaceholder}
          message={<CantFindColumnMessage />}
          messageMaxW="100%"
          title="No columns match your search"
          variant="minimal"
        />
      ) : hasCategory && isEventModel ? (
        <EmptyState
          message="Enter the name of an expected column to map it."
          title="No event properties yet"
          variant="minimal"
        />
      ) : (
        <EmptyState
          imageUrl={boxPlaceholder}
          message={<CantFindColumnMessage />}
          messageMaxW="100%"
          title="No columns available"
          variant="minimal"
        />
      )}
    </Column>
  );
}
